import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ExternalLink, Check, Zap, Building2, AlertCircle, X, FileText, CreditCard, Download, RefreshCw } from 'lucide-react';
import {
  fetchBillingInfo, createCheckout, signPrivacyPolicy,
  fetchCompany, saveCompany, fetchInvoices, fetchPaymentMethod,
  createSetupIntent, updatePaymentMethod,
  type BillingInfo, type CheckoutRequest, type CompanyData, type Invoice,
} from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLang } from '../lib/i18n';
import { format, fromUnixTime } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '');

// ── Helpers ───────────────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, { nl: string; en: string }> = {
  trial:        { nl: 'Trial',        en: 'Trial' },
  starter:      { nl: 'Starter',      en: 'Starter' },
  msp_complete: { nl: 'MSP Complete', en: 'MSP Complete' },
};

function fmtAmount(cents: number, currency: string) {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

function fmtDate(unix: number, lang: 'nl' | 'en') {
  return format(fromUnixTime(unix), 'dd/MM/yyyy', { locale: lang === 'nl' ? nl : enUS });
}

function CardBrandIcon({ brand }: { brand: string }) {
  const brands: Record<string, string> = { visa: '💳', mastercard: '💳', amex: '💳', discover: '💳' };
  return <span>{brands[brand] ?? '💳'}</span>;
}

// ── Plan definitions (same as before) ────────────────────────────────────────

interface PlanDef { key: 'trial' | 'starter' | 'msp_complete'; price: string; credits: number; color: string; featuresNl: string[]; featuresEn: string[]; }
const PLANS: PlanDef[] = [
  { key: 'starter',      price: '€199', credits: 33, color: 'brand',  featuresNl: ['33 verificaties/maand','Automatisch bijkopen','PDF & CSV rapporten','E-mailrapportage','E-mailondersteuning'], featuresEn: ['33 verifications/month','Automatic top-up','PDF & CSV reports','Email reporting','Email support'] },
  { key: 'msp_complete', price: '€399', credits: 99, color: 'purple', featuresNl: ['99 verificaties/maand','Automatisch bijkopen','PDF & CSV rapporten','E-mailrapportage','Prioriteitsondersteuning','Multi-app beheer'], featuresEn: ['99 verifications/month','Automatic top-up','PDF & CSV reports','Email reporting','Priority support','Multi-app management'] },
];

// ── Checkout Modal ─────────────────────────────────────────────────────────────
function CheckoutModal({ plan, onClose }: { plan: PlanDef; onClose: () => void }) {
  const { t, lang } = useLang();
  const bt = t.billing;
  const [qty, setQty] = useState(25);
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [loading, setLoading] = useState(false);

  const proceed = async () => {
    if (!consent) { setConsentError(true); return; }
    setLoading(true);
    try {
      const res = await createCheckout({ plan: plan.key as CheckoutRequest['plan'], auto_topup_quantity: qty, auto_topup_consent: true });
      window.location.href = res.checkout_url;
    } catch { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{bt.modal.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
            <div>
              <p className="font-semibold text-gray-900">{PLAN_LABELS[plan.key][lang]}</p>
              <p className="text-sm text-gray-500">{plan.credits} {bt.plans.verificationsPerMonth}</p>
            </div>
            <p className="text-xl font-bold text-gray-900">{plan.price}<span className="text-sm font-normal text-gray-500">{bt.plans.perMonth}</span></p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-sm text-gray-800">{bt.modal.autoTopupTitle}</p>
            <p className="text-sm text-gray-500">{bt.modal.autoTopupDesc}</p>
            <label className="block text-sm font-medium text-gray-700 mt-3">{bt.modal.qtyLabel}</label>
            <div className="flex items-center gap-3">
              <Input type="number" min={10} max={500} value={qty} onChange={e => setQty(Math.max(10, parseInt(e.target.value) || 10))} className="w-28" />
              <span className="text-sm text-gray-500">{bt.modal.qtyMin}</span>
            </div>
            <p className="text-sm text-gray-600 bg-blue-50 rounded-lg px-3 py-2">≈ €{(qty * 4).toFixed(0)} {lang === 'nl' ? 'per aanvulling' : 'per top-up'}</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={e => { setConsent(e.target.checked); setConsentError(false); }} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
            <span className="text-sm text-gray-700">{bt.modal.consentLabel}</span>
          </label>
          {consentError && <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{bt.modal.consentRequired}</p>}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <Button variant="secondary" onClick={onClose} className="flex-1">{bt.modal.cancelBtn}</Button>
          <Button onClick={proceed} loading={loading} className="flex-1">{bt.modal.proceedBtn}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Privacy Policy Banner ──────────────────────────────────────────────────────
function PolicyBanner({ onSigned }: { onSigned: () => void }) {
  const { t } = useLang();
  const bt = t.billing.policy;
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  const sign = async () => {
    setSigning(true);
    try { await signPrivacyPolicy(); setSigned(true); setTimeout(onSigned, 1200); }
    catch { setSigning(false); }
  };

  if (signed) return (
    <div className="rounded-xl bg-green-50 border border-green-200 px-5 py-4 flex items-center gap-3">
      <Check className="h-5 w-5 text-green-600 shrink-0" />
      <p className="text-sm font-medium text-green-800">{bt.signed}</p>
    </div>
  );

  return (
    <div className="rounded-xl bg-amber-50 border border-amber-200 px-5 py-5 space-y-3">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-900">{bt.bannerTitle}</p>
          <p className="text-sm text-amber-800 mt-0.5">{bt.bannerDesc}</p>
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="secondary" size="sm" onClick={() => window.open('/privacy-policy.html', '_blank')}>
          <ExternalLink className="h-3.5 w-3.5" />{bt.viewBtn}
        </Button>
        <Button size="sm" onClick={sign} loading={signing}>
          <Check className="h-3.5 w-3.5" />{signing ? bt.signing : bt.signBtn}
        </Button>
      </div>
    </div>
  );
}

// ── Subscription Tab ───────────────────────────────────────────────────────────
function SubscriptionTab({ info, onSelectPlan }: { info: BillingInfo; onSelectPlan: (p: PlanDef) => void }) {
  const { t, lang } = useLang();
  const bt = t.billing;
  const isTrial = info.plan === 'trial';
  const statusColor = {
    active: 'bg-green-100 text-green-800', trialing: 'bg-gray-100 text-gray-700',
    past_due: 'bg-red-100 text-red-800', canceled: 'bg-gray-100 text-gray-600', incomplete: 'bg-yellow-100 text-yellow-800',
  }[info.subscription_status] ?? 'bg-gray-100 text-gray-700';
  const statusLabel = bt.status[info.subscription_status as keyof typeof bt.status] ?? info.subscription_status;

  return (
    <div className="space-y-6">
      {/* Current plan summary */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="h-4 w-4 text-brand-600" />{bt.currentPlan.title}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {isTrial && <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">{bt.currentPlan.trialNote}</p>}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-gray-50 px-4 py-3 space-y-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{lang === 'nl' ? 'Pakket' : 'Plan'}</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900">{PLAN_LABELS[info.plan]?.[lang] ?? info.plan}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>{statusLabel}</span>
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 px-4 py-3 space-y-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{bt.currentPlan.creditsRemaining}</p>
              <p className="text-2xl font-bold text-brand-700">{info.credits_remaining}</p>
            </div>
            {!isTrial && <>
              <div className="rounded-lg bg-gray-50 px-4 py-3 space-y-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">{bt.currentPlan.planCredits}</p>
                <p className="text-2xl font-bold text-gray-900">{info.plan_credits}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-4 py-3 space-y-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">{bt.currentPlan.autoTopup}</p>
                <p className="font-semibold text-gray-900">{info.auto_topup_quantity} <span className="text-xs font-normal text-gray-500">{bt.currentPlan.autoTopupCredits}</span></p>
              </div>
            </>}
          </div>
        </CardContent>
      </Card>

      {/* Plan cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">{bt.plans.title}</h2>
        <p className="text-sm text-gray-500 mb-4">{bt.plans.subtitle}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PLANS.map(plan => {
            const isCurrent = info.plan === plan.key;
            const isPurple = plan.color === 'purple';
            const features = lang === 'nl' ? plan.featuresNl : plan.featuresEn;
            const borderClass = isCurrent ? (isPurple ? 'border-purple-400 ring-2 ring-purple-200' : 'border-brand-400 ring-2 ring-brand-100') : 'border-gray-200 hover:border-gray-300';
            const headerBg = isPurple ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white' : 'bg-gradient-to-br from-brand-600 to-brand-700 text-white';
            return (
              <div key={plan.key} className={`rounded-xl border ${borderClass} overflow-hidden flex flex-col transition-shadow hover:shadow-md`}>
                <div className={`${headerBg} px-5 py-5`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-lg">{PLAN_LABELS[plan.key][lang]}</p>
                    {isCurrent && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white/20">{bt.plans.currentBadge}</span>}
                  </div>
                  <p className="text-3xl font-bold">{plan.price}<span className="text-sm font-normal text-white/70">{bt.plans.perMonth}</span></p>
                  <p className="text-sm mt-1 text-white/80">{plan.credits} {bt.plans.verificationsPerMonth}</p>
                </div>
                <div className="px-5 py-4 flex-1 space-y-2">
                  {features.map(f => (
                    <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check className={`h-4 w-4 shrink-0 ${isPurple ? 'text-purple-500' : 'text-brand-500'}`} />
                      {f}
                    </div>
                  ))}
                </div>
                <div className="px-5 pb-5">
                  <Button
                    className={`w-full ${isPurple ? '!bg-purple-600 hover:!bg-purple-700' : ''}`}
                    onClick={() => onSelectPlan(plan)}
                  >
                    {isCurrent && info.subscription_status === 'active' ? bt.plans.switchBtn : bt.plans.subscribeBtn}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Company Tab ────────────────────────────────────────────────────────────────
function CompanyTab() {
  const { t } = useLang();
  const bt = t.billing.company;
  const [form, setForm] = useState<CompanyData>({ company_name: '', vat_number: '', address_line1: '', address_city: '', address_postal_code: '', address_country: 'BE' });
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState(false);

  const { isLoading, isError, data: companyData } = useQuery({
    queryKey: ['billing-company'],
    queryFn: fetchCompany,
  });

  useEffect(() => {
    if (companyData) setForm(companyData);
  }, [companyData]);

  const { mutate, isPending: saving } = useMutation({
    mutationFn: saveCompany,
    onSuccess: () => { setSaved(true); setSaveErr(false); setTimeout(() => setSaved(false), 3000); },
    onError: () => setSaveErr(true),
  });

  const field = (key: keyof CompanyData, label: string, placeholder?: string, hint?: string) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <Input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  if (isLoading) return <div className="flex items-center gap-2 text-sm text-gray-400"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />{t.billing.loading}</div>;
  if (isError) return <p className="text-sm text-red-500">{bt.loadError}</p>;

  return (
    <Card>
      <CardHeader><CardTitle>{bt.title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">{bt.subtitle}</p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {field('company_name', bt.companyName)}
          {field('vat_number', bt.vatNumber, 'BE0123456789', bt.vatHint)}
          <div className="sm:col-span-2">{field('address_line1', bt.address)}</div>
          {field('address_city', bt.city)}
          {field('address_postal_code', bt.zip)}
          {field('address_country', bt.country)}
        </div>
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={() => { setSaved(false); setSaveErr(false); mutate(form); }} loading={saving as boolean}>
            {saving ? bt.saving : bt.save}
          </Button>
          {saved && <span className="text-sm text-green-600 font-medium">{bt.saved}</span>}
          {saveErr && <span className="text-sm text-red-500">{bt.saveError}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Invoices Tab ───────────────────────────────────────────────────────────────
function InvoicesTab({ hasSubscription }: { hasSubscription: boolean }) {
  const { t, lang } = useLang();
  const bt = t.billing.invoices;

  const { data: invoices = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: fetchInvoices,
    staleTime: 60 * 1000,
  });

  const statusLabel = (s: string | null) => {
    if (s === 'paid')  return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-medium">{bt.statusPaid}</span>;
    if (s === 'open')  return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium">{bt.statusOpen}</span>;
    if (s === 'void')  return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 font-medium">{bt.statusVoid}</span>;
    return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 font-medium">{bt.statusDraft}</span>;
  };

  if (!hasSubscription) return (
    <Card><CardContent className="py-8 text-center text-sm text-gray-400">{bt.noSubscription}</CardContent></Card>
  );
  if (isLoading) return <div className="flex items-center gap-2 text-sm text-gray-400"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />{t.billing.loading}</div>;
  if (isError) return <div className="flex items-center gap-3"><p className="text-sm text-red-500">{bt.loadError}</p><Button variant="ghost" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" /></Button></div>;
  if (invoices.length === 0) return <Card><CardContent className="py-8 text-center text-sm text-gray-400">{bt.noInvoices}</CardContent></Card>;

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-brand-600" />{bt.title}</CardTitle></CardHeader>
      <CardContent className="p-0">
        <p className="text-sm text-gray-500 px-6 pb-4">{bt.subtitle}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{bt.colDate}</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{bt.colNumber}</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{bt.colPeriod}</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{bt.colAmount}</th>
                <th className="text-center px-6 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{bt.colStatus}</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv: Invoice, i: number) => (
                <tr key={inv.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-6 py-3 text-gray-700">{fmtDate(inv.date, lang)}</td>
                  <td className="px-6 py-3 text-gray-500 font-mono text-xs">{inv.number ?? '—'}</td>
                  <td className="px-6 py-3 text-gray-500 text-xs">{fmtDate(inv.period_from, lang)} – {fmtDate(inv.period_to, lang)}</td>
                  <td className="px-6 py-3 text-right font-medium text-gray-900">{fmtAmount(inv.amount, inv.currency)}</td>
                  <td className="px-6 py-3 text-center">{statusLabel(inv.status)}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      {inv.pdf_url && (
                        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium">
                          <Download className="h-3.5 w-3.5" />{bt.download}
                        </a>
                      )}
                      {inv.hosted_url && (
                        <a href={inv.hosted_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                          <ExternalLink className="h-3 w-3" />{bt.open}
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Card Update Form (inside Elements) ────────────────────────────────────────
function CardUpdateForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const { t } = useLang();
  const bt = t.billing.payment;
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Create setup intent
      const { client_secret } = await createSetupIntent();

      // 2. Confirm card setup
      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card },
      });

      if (result.error) {
        setError(result.error.message ?? bt.saveError);
        setLoading(false);
        return;
      }

      const pmId = result.setupIntent?.payment_method as string;

      // 3. Set as default
      await updatePaymentMethod(pmId);
      onSaved();
    } catch {
      setError(bt.saveError);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{bt.cardLabel}</label>
        <div className="border border-gray-300 rounded-lg px-4 py-3 bg-white focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 transition-shadow">
          <CardElement options={{ style: { base: { fontSize: '16px', color: '#111827', '::placeholder': { color: '#9CA3AF' } } } }} />
        </div>
        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          <Zap className="h-3 w-3" />{bt.cardHint}
        </p>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-3">
        <Button variant="secondary" onClick={onCancel}>{bt.cancelBtn}</Button>
        <Button onClick={submit} loading={loading}>{loading ? bt.saving : bt.saveBtn}</Button>
      </div>
    </div>
  );
}

// ── Payment Tab ────────────────────────────────────────────────────────────────
function PaymentTab({ info }: { info: BillingInfo }) {
  const { t } = useLang();
  const bt = t.billing.payment;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const hasSubscription = info.plan !== 'trial';

  const { data: method, isLoading, isError, refetch } = useQuery({
    queryKey: ['billing-payment'],
    queryFn: fetchPaymentMethod,
    enabled: hasSubscription,
    staleTime: 60 * 1000,
  });

  const handleSaved = useCallback(() => {
    setEditing(false);
    setSavedMsg(true);
    queryClient.invalidateQueries({ queryKey: ['billing-payment'] });
    setTimeout(() => setSavedMsg(false), 3000);
  }, [queryClient]);

  if (!hasSubscription) return (
    <Card><CardContent className="py-8 text-center text-sm text-gray-400">{bt.noSubscription}</CardContent></Card>
  );

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-brand-600" />{bt.title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">{bt.subtitle}</p>

        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-400"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />{t.billing.loading}</div>}
        {isError && <p className="text-sm text-red-500">{bt.loadError}</p>}

        {!isLoading && !isError && !editing && (
          <div className="space-y-4">
            {method ? (
              <div className="flex items-center gap-4 rounded-lg border border-gray-200 px-5 py-4 bg-gray-50">
                <CardBrandIcon brand={method.brand} />
                <div>
                  <p className="font-medium text-gray-900 capitalize">{method.brand} •••• {method.last4}</p>
                  <p className="text-sm text-gray-500">{bt.expires} {method.exp_month}/{method.exp_year}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">{bt.noMethod}</p>
            )}
            {savedMsg && <p className="text-sm text-green-600 font-medium">{bt.saved}</p>}
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <CreditCard className="h-4 w-4" />{bt.updateBtn}
            </Button>
          </div>
        )}

        {editing && (
          <Elements stripe={stripePromise}>
            <CardUpdateForm onSaved={handleSaved} onCancel={() => setEditing(false)} />
          </Elements>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Billing Page ──────────────────────────────────────────────────────────
export function Billing() {
  const { t } = useLang();
  const bt = t.billing;
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'subscription' | 'company' | 'invoices' | 'payment'>('subscription');
  const [selectedPlan, setSelectedPlan] = useState<PlanDef | null>(null);
  const [showPolicySigned, setShowPolicySigned] = useState(false);

  const justPaid = new URLSearchParams(window.location.search).get('billing_success') === '1';
  useEffect(() => {
    if (justPaid) {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['billing-info'] }), 2000);
    }
  }, [justPaid, queryClient]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['billing-info'],
    queryFn: fetchBillingInfo,
    staleTime: 30 * 1000,
  });

  if (isLoading) return (
    <div className="flex items-center gap-2 text-sm text-gray-400 mt-8">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />{bt.loading}
    </div>
  );

  if (isError || !data) return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{bt.title}</h1>
      <div className="rounded-xl bg-red-50 border border-red-100 px-5 py-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
        <p className="text-sm text-red-700">{bt.loadError}</p>
      </div>
      <Button variant="secondary" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />{t.reports.download.retry}</Button>
    </div>
  );

  const hasSubscription = data.plan !== 'trial';
  const needsPolicy = !data.privacy_policy_signed && hasSubscription && !showPolicySigned;

  const tabs = [
    { id: 'subscription', label: bt.tabs.subscription },
    { id: 'company',      label: bt.tabs.company },
    { id: 'invoices',     label: bt.tabs.invoices },
    { id: 'payment',      label: bt.tabs.payment },
  ] as const;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{bt.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{bt.subtitle}</p>
      </div>

      {/* Stripe success banner */}
      {justPaid && (
        <div className="rounded-xl bg-green-50 border border-green-200 px-5 py-4 flex items-center gap-3">
          <Check className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="font-semibold text-green-900">{t.lang.nl === 'Nederlands' ? 'Betaling geslaagd! Welkom bij XyloSnap.' : 'Payment successful! Welcome to XyloSnap.'}</p>
            <p className="text-sm text-green-700">{t.lang.nl === 'Nederlands' ? 'Uw abonnement wordt geactiveerd. Dit kan enkele seconden duren.' : 'Your subscription is being activated. This may take a few seconds.'}</p>
          </div>
        </div>
      )}

      {/* Privacy policy banner */}
      {needsPolicy && <PolicyBanner onSigned={() => { setShowPolicySigned(true); queryClient.invalidateQueries({ queryKey: ['billing-info'] }); }} />}
      {showPolicySigned && !needsPolicy && (
        <div className="rounded-xl bg-green-50 border border-green-200 px-5 py-4 flex items-center gap-3">
          <Check className="h-5 w-5 text-green-600 shrink-0" />
          <p className="text-sm font-medium text-green-800">{bt.policy.signed}</p>
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <div className="flex gap-0 -mb-px">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${tab === id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {tab === 'subscription' && <SubscriptionTab info={data} onSelectPlan={setSelectedPlan} />}
      {tab === 'company'      && <CompanyTab />}
      {tab === 'invoices'     && <InvoicesTab hasSubscription={hasSubscription} />}
      {tab === 'payment'      && <PaymentTab info={data} />}

      {/* Checkout modal */}
      {selectedPlan && <CheckoutModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />}
    </div>
  );
}
